import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import DataUsage from '../../components/DataUsage/DataUsage';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { BsCalendar3 } from 'react-icons/bs';
import Header from '../../components/Header/Header';
import StatCard from '../../components/StatCard/StatCard';
import MapDevicesPanel from '../../components/MapDevicesPanel/MapDevicesPanel';
import DevicesByDistrict from '../../components/DevicesByDistrict/DevicesByDistrict';
import { useOverviewData } from '../../hooks/useOverviewData';
import { useAppsByGrade } from '../../hooks/useAppsByGrade';
import { ENDPOINTS } from '../../api/api';
import { StatCardSkeleton, SectionSkeleton } from '../../components/Skeleton/Skeleton';
import styles from './Overview.module.css';

const BREADCRUMBS = [{ label: 'Overview' }];

const GRADES = ['1', '2', '8', '10', '12'];

function Overview() {
  const [grade, setGrade] = useState('All');
  const [lostStolen, setLostStolen] = useState([]);
  const { loading, error, stats = {}, carriers = [], devicesByDistrict = [], dataUsage = [], refresh } = useOverviewData();

  useEffect(() => {
    fetch(ENDPOINTS.lostStolenByDistrict)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setLostStolen(data); })
      .catch(() => {});
  }, []);

  const { apps, loading: appsLoading } = useAppsByGrade(grade === 'All' ? 'All' : grade);

  if (error) {
    return (
      <>
        <Header breadcrumbs={BREADCRUMBS} />
        <div style={{ color: '#E53535', padding: '2rem' }}>Failed to load overview: {error}</div>
      </>
    );
  }

  return (
    <>
      <Header breadcrumbs={BREADCRUMBS} />

      <div className={styles.page}>

        <div className={styles.titleRow}>
          <h1 className={styles.pageTitle}>Overview</h1>
          <div className={styles.titleActions}>
            <RefreshButton onClick={refresh} loading={loading} />
          </div>
        </div>

        {/* <div className={styles.dateFilter}>
          <BsCalendar3 size={13} />
          <select defaultValue="Last 30 days">
            <option>Last 30 days</option>
            <option>Last 7 days</option>
            <option>Last 90 days</option>
          </select>
        </div> */}

        <div className={styles.statsGrid}>
          {loading
            ? [0, 1, 2, 3].map((i) => <StatCardSkeleton key={i} />)
            : [
                { label: 'Devices Deployed', value: stats.devices   ?? '—' },
                { label: 'Schools',          value: stats.schools   ?? '—' },
                { label: 'Circuits',         value: stats.circuits  ?? '—' },
                { label: 'Districts',        value: stats.districts ?? '—' },
              ].map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>SIM Carrier Distribution</div>
          <div className={styles.sectionCard}>
            <div className={styles.carrierBar}>
              {carriers.map((c) => (
                <div key={c.name} className={styles.carrierSegment} style={{ width: `${c.pct}%`, background: c.color }} />
              ))}
            </div>
            <div className={styles.carrierStats}>
              {carriers.map((c) => (
                <div key={c.name} className={styles.carrierItem}>
                  <div className={styles.carrierName}>
                    <span className={styles.carrierDot} style={{ background: c.color }} />
                    {c.name}
                  </div>
                  <div className={styles.carrierValue}>
                    {(c.value ?? 0).toLocaleString()}
                    <span className={styles.carrierPct}>{c.pct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.twoCol}>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Devices by District</div>
            {loading ? <SectionSkeleton rows={8} /> : <DevicesByDistrict districts={devicesByDistrict} linkBasePath="/district" />}
          </div>

          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Data Usage</div>
            {loading ? <SectionSkeleton rows={4} /> : <DataUsage data={dataUsage} />}
          </div>
        </div>

        <MapDevicesPanel />

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Application by Grade</div>
          <div className={styles.tableCard}>
            <div className={styles.tableCardHeader}>
              <div className={styles.gradeFilter}>
                <select value={grade} onChange={(e) => setGrade(e.target.value)}>
                  <option value="All">All</option>
                  {GRADES.map((g) => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Version</th>
                  <th>Installs</th>
                  <th>Adoption</th>
                </tr>
              </thead>
              <tbody>
                {appsLoading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '1.5rem' }}>Loading…</td></tr>
                ) : apps.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '1.5rem' }}>No data</td></tr>
                ) : apps.slice(0, 10).map((app, i) => (
                  <tr key={i}>
                    <td>{app.name}</td>
                    <td>{app.version ?? '—'}</td>
                    <td>{typeof app.installs === 'number' ? app.installs.toLocaleString() : app.installs ?? '—'}</td>
                    <td>
                      <div className={styles.adoptionCell}>
                        <span>{app.adoption ?? 0}%</span>
                        <div className={styles.adoptionBar}>
                          <div className={styles.adoptionFill} style={{ width: `${app.adoption ?? 0}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Lost or Stolen Devices by District</div>
          <div className={styles.sectionCard}>
            <div className={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={lostStolen} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                  <XAxis dataKey="district" tick={{ fill: 'var(--color-text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ fill: 'var(--color-text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', borderRadius: 8, color: 'var(--color-text-primary)', fontSize: 12 }} cursor={{ fill: 'rgba(63,183,96,0.06)' }} />
                  <Bar dataKey="count" fill="#3FB760" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default Overview;
